import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InputField from '../components/InputField';
import InputMask from 'react-input-mask';
import Header from '../others/Header';
import config from '../config/config';
import ConfirmModal from '../others/ConfirmModal';
import { useToast } from '../components/ToastContext';
import UsuariosModal from '../others/UsuariosModal';
import PlanosArmazenamento from '../components/PlanosArmazenamento';
import {
  FaBuilding,
  FaIdCard,
  FaUser,
  FaPhone,
  FaCalendar,
  FaEdit,
  FaTrash,
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaHdd
} from 'react-icons/fa';

function EmpresaRegistration() {
  const [formData, setFormData] = useState({
    cnpj: '',
    nomeFantasia: '',
    razaoSocial: '',
    ddd: '',
    telefone: '',
    nomeContato: '',
    tipoJuridico: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    periodoDias: 30,
    ativa: true,
    tipoPlano: 'TESTE',
    diaCobranca: 1,
    tipoAcesso: 'LOCAL',
    idPlanoArmazenamento: ''
  });

  const [empresas, setEmpresas] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editEmpresaId, setEditEmpresaId] = useState(null);
  const [errors, setErrors] = useState({});
  const { API_BASE_URL } = config;
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [empresaToDelete, setEmpresaToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [empresaEmEdicao, setEmpresaEmEdicao] = useState(null);
  const [isUsuariosModalOpen, setIsUsuariosModalOpen] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosLoading, setUsuariosLoading] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [planosArmazenamento, setPlanosArmazenamento] = useState([]);
  const [novoPlanoAberto, setNovoPlanoAberto] = useState(false);
  const [novoPlanoGb, setNovoPlanoGb] = useState('');
  const [criandoPlano, setCriandoPlano] = useState(false);

  const { addToast } = useToast();

  const formatarCep = (valor) => {
    const num = (valor || '').replace(/\D/g, '').slice(0, 8);
    return num.length <= 5 ? num : `${num.slice(0, 5)}-${num.slice(5)}`;
  };

  // Busca automática de endereço pelo CEP (ViaCEP) — não bloqueia o formulário se falhar
  const buscarCepEmpresa = async (cepValor) => {
    const cep = (cepValor || '').replace(/\D/g, '');
    if (cep.length !== 8) return;

    setBuscandoCep(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
      if (response.data && !response.data.erro) {
        setFormData(prev => ({
          ...prev,
          logradouro: response.data.logradouro || prev.logradouro,
          bairro: response.data.bairro || prev.bairro,
          cidade: response.data.localidade || prev.cidade,
          uf: response.data.uf || prev.uf
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setBuscandoCep(false);
    }
  };

  // Handler único para os campos do formulário
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteConfirmation = (empresa) => {
    setEmpresaToDelete(empresa);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (empresaToDelete) {
      await handleDelete(empresaToDelete);
      setIsConfirmModalOpen(false);
      setEmpresaToDelete(null);
    }
  };

  const fetchUsuarios = async (empresaId, empresaNome) => {
    const token = localStorage.getItem("jwtToken");
    setUsuariosLoading(true);
    setSelectedEmpresa(empresaNome);

    try {
      const response = await axios.get(`${API_BASE_URL}/usuarios/${empresaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsuarios(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao buscar usuários:', error);
        addToast('error', 'Erro ao carregar usuários. Tente novamente.');
      }
    } finally {
      setUsuariosLoading(false);
    }
  };

  const handleOpenUsuariosModal = (empresa) => {
    setIsUsuariosModalOpen(true);
    fetchUsuarios(empresa.idEmpresa, empresa.nomeFantasia);
  };

  const handleCloseUsuariosModal = () => {
    setIsUsuariosModalOpen(false);
    setUsuarios([]);
    setSelectedEmpresa(null);
  };

  const handleUnauthorized = () => {
    window.location.href = "/login";
  };

  const fetchEmpresas = async () => {
    const token = localStorage.getItem("jwtToken");
    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/empresas`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setEmpresas(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao buscar empresas:', error);
        addToast('error', 'Erro ao carregar empresas. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanosArmazenamento = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/planos-armazenamento`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem("jwtToken")}` }
      });
      setPlanosArmazenamento(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar planos de armazenamento:', error);
    }
  };

  // "5 GB" quando o nome já diz a capacidade; senão "Básico (5 GB)"
  const rotuloPlano = (plano) =>
    plano.nome === `${plano.capacidadeGb} GB` ? plano.nome : `${plano.nome} (${plano.capacidadeGb} GB)`;

  const criarPlanoRapido = async () => {
    const gb = parseInt(novoPlanoGb, 10);
    if (!gb || gb <= 0) {
      addToast('error', 'Informe a quantidade de GB (maior que zero).');
      return;
    }
    // Se já existe um plano ativo com essa capacidade, só seleciona ele
    const existente = planosArmazenamento.find((p) => p.capacidadeGb === gb && p.ativo !== false);
    if (existente) {
      handleInputChange('idPlanoArmazenamento', String(existente.id));
      setNovoPlanoAberto(false);
      setNovoPlanoGb('');
      return;
    }
    setCriandoPlano(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/planos-armazenamento`,
        { nome: `${gb} GB`, capacidadeGb: gb },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem("jwtToken")}` } });
      await fetchPlanosArmazenamento();
      handleInputChange('idPlanoArmazenamento', String(response.data.id));
      setNovoPlanoAberto(false);
      setNovoPlanoGb('');
    } catch (error) {
      addToast('error', typeof error.response?.data === 'string' ? error.response.data : 'Erro ao criar opção de armazenamento.');
    } finally {
      setCriandoPlano(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (!token) {
      handleUnauthorized();
    } else {
      fetchEmpresas();
      fetchPlanosArmazenamento();
    }
  }, []);

  const validateFields = () => {
    const newErrors = {};

    if (!formData.cnpj) newErrors.cnpj = 'CNPJ é obrigatório';
    if (!formData.nomeFantasia) newErrors.nomeFantasia = 'Nome Fantasia é obrigatório';
    if (!formData.razaoSocial) newErrors.razaoSocial = 'Razão Social é obrigatória';
    if (!formData.ddd) newErrors.ddd = 'DDD é obrigatório';
    if (!formData.telefone) newErrors.telefone = 'Telefone é obrigatório';
    if (!formData.nomeContato) newErrors.nomeContato = 'Nome do Contato é obrigatório';
    if (!formData.tipoJuridico) newErrors.tipoJuridico = 'Tipo Jurídico é obrigatório';
    if (formData.periodoDias <= 0) newErrors.periodoDias = 'Período deve ser maior que zero';
    
    if (formData.tipoPlano === 'MENSAL' && (formData.diaCobranca < 1 || formData.diaCobranca > 31)) {
      newErrors.diaCobranca = 'Dia da cobrança deve ser entre 1 e 31';
    }

    if (formData.tipoAcesso === 'WEB' && !formData.idPlanoArmazenamento) {
      newErrors.idPlanoArmazenamento = 'Selecione o armazenamento contratado';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateFields()) return;

    const empresaData = {
      cnpj: formData.cnpj.replace(/\D/g, ''),
      nomeFantasia: formData.nomeFantasia,
      razaoSocial: formData.razaoSocial,
      ddd: formData.ddd,
      telefone: formData.telefone,
      nomeContato: formData.nomeContato,
      tipoJuridico: formData.tipoJuridico,
      cep: formData.cep,
      logradouro: formData.logradouro,
      numero: formData.numero,
      complemento: formData.complemento,
      bairro: formData.bairro,
      cidade: formData.cidade,
      uf: formData.uf,
      periodoDias: formData.periodoDias,
      ativa: formData.ativa,
      tipoPlano: formData.tipoPlano,
      diaCobranca: formData.tipoPlano === 'MENSAL' ? formData.diaCobranca : null,
      tipoAcesso: formData.tipoAcesso,
      planoArmazenamento: formData.tipoAcesso === 'WEB' && formData.idPlanoArmazenamento
        ? { id: Number(formData.idPlanoArmazenamento) }
        : null
    };

    try {
      const token = localStorage.getItem("jwtToken");
      setLoading(true);

      if (isEditing) {
        await axios.put(`${API_BASE_URL}/empresas/${editEmpresaId}`, empresaData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setIsEditing(false);
        setEditEmpresaId(null);
      } else {
        await axios.post(`${API_BASE_URL}/empresas`, empresaData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
      
      await fetchEmpresas();
      resetForm();
      addToast('success', isEditing ? 'Empresa atualizada com sucesso!' : 'Empresa cadastrada com sucesso!');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao registrar ou editar empresa:', error);
        addToast('error', 'Erro ao salvar empresa. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      cnpj: '',
      nomeFantasia: '',
      razaoSocial: '',
      ddd: '',
      telefone: '',
      nomeContato: '',
      tipoJuridico: '',
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      uf: '',
      periodoDias: 30,
      ativa: true,
      tipoPlano: 'TESTE',
      diaCobranca: 1,
      tipoAcesso: 'LOCAL',
      idPlanoArmazenamento: ''
    });
    setErrors({});
  };

  const handleEdit = (empresa) => {
    setFormData({
      cnpj: empresa.cnpj || '',
      nomeFantasia: empresa.nomeFantasia || '',
      razaoSocial: empresa.razaoSocial || '',
      ddd: empresa.ddd || '',
      telefone: empresa.telefone || '',
      nomeContato: empresa.nomeContato || '',
      tipoJuridico: empresa.tipoJuridico || '',
      cep: empresa.cep || '',
      logradouro: empresa.logradouro || '',
      numero: empresa.numero || '',
      complemento: empresa.complemento || '',
      bairro: empresa.bairro || '',
      cidade: empresa.cidade || '',
      uf: empresa.uf || '',
      periodoDias: empresa.periodoDias || 30,
      ativa: empresa.ativa !== undefined ? empresa.ativa : true,
      tipoPlano: empresa.tipoPlano || 'TESTE',
      diaCobranca: empresa.diaCobranca || 1,
      // Empresas antigas vêm sem tipo de acesso: são locais
      tipoAcesso: empresa.tipoAcesso || 'LOCAL',
      idPlanoArmazenamento: empresa.planoArmazenamento?.id ? String(empresa.planoArmazenamento.id) : ''
    });

    setIsEditing(true);
    setEditEmpresaId(empresa.idEmpresa);
    setEmpresaEmEdicao(empresa);
    
    document.getElementById('form-empresa').scrollIntoView({ behavior: 'smooth' });
  };

  const handleDelete = async (empresa) => {
    try {
      const token = localStorage.getItem("jwtToken");
      setLoading(true);
      
      await axios.delete(`${API_BASE_URL}/empresas/${empresa.idEmpresa}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      await fetchEmpresas();
      addToast('success', 'Empresa excluída com sucesso!');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao excluir empresa:', error);
        addToast('error', 'Erro ao excluir empresa. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditEmpresaId(null);
    setEmpresaEmEdicao(null);
    resetForm();
  };

  // Funções de formatação
  const formatarCNPJ = (cnpj) => {
    if (!cnpj) return 'Não informado';
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length === 14) {
      return `${cnpjLimpo.substring(0, 2)}.${cnpjLimpo.substring(2, 5)}.${cnpjLimpo.substring(5, 8)}/${cnpjLimpo.substring(8, 12)}-${cnpjLimpo.substring(12)}`;
    }
    return cnpj;
  };

  const formatarTelefone = (telefone) => {
    if (!telefone) return 'Não informado';
    const telefoneLimpo = telefone.replace(/\D/g, '');
    if (telefoneLimpo.length === 9) {
      return `${telefoneLimpo.substring(0, 5)}-${telefoneLimpo.substring(5)}`;
    } else if (telefoneLimpo.length === 8) {
      return `${telefoneLimpo.substring(0, 4)}-${telefoneLimpo.substring(4)}`;
    }
    return telefone;
  };

  const formatarEnderecoEmpresa = (empresa) => {
    const partes = [];
    if (empresa.logradouro) {
      let linha = empresa.logradouro;
      if (empresa.numero) linha += `, ${empresa.numero}`;
      if (empresa.complemento) linha += ` - ${empresa.complemento}`;
      partes.push(linha);
    }
    if (empresa.bairro) partes.push(empresa.bairro);
    if (empresa.cidade || empresa.uf) {
      partes.push([empresa.cidade, empresa.uf].filter(Boolean).join('/'));
    }
    return partes.join(' - ') || 'Não informado';
  };

  const formatarTipoPlano = (tipoPlano) => {
    const tipos = {
      'TESTE': 'Teste',
      'DEFINITIVO': 'Definitivo',
      'MENSAL': 'Mensal'
    };
    return tipos[tipoPlano] || tipoPlano;
  };

  const getStatusEmpresa = (empresa) => {
    if (!empresa.ativa) return { status: 'Inativa', cor: '#dc3545', icone: FaTimesCircle };
    
    if (!empresa.dataInicioPeriodo) {
      return { status: 'Ativa (não iniciado)', cor: '#17a2b8', icone: FaInfoCircle };
    }
    
    const hoje = new Date();
    const fim = empresa.dataFimPeriodo ? new Date(empresa.dataFimPeriodo) : null;
    
    if (fim && hoje > fim) return { status: 'Período expirado', cor: '#ffc107', icone: FaExclamationTriangle };
    
    return { status: 'Ativa', cor: '#28a745', icone: FaCheckCircle };
  };

  const getDiasRestantes = (empresa) => {
    if (!empresa.ativa || !empresa.dataFimPeriodo) return '-';
    
    const hoje = new Date();
    const fim = new Date(empresa.dataFimPeriodo);
    
    if (hoje > fim) return 'Expirado';
    
    const diffTime = fim - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return `${diffDays} dias`;
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'Não definida';
    try {
      const data = new Date(dataString);
      return data.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  };

  // Componente Card para cada empresa
  const EmpresaCard = ({ empresa }) => {
    const statusInfo = getStatusEmpresa(empresa);
    const StatusIcon = statusInfo.icone;

    return (
      <div style={cardStyles.card}>
        {/* Header do Card */}
        <div style={cardStyles.cardHeader}>
          <div style={cardStyles.empresaInfo}>
            <FaBuilding style={cardStyles.empresaIcon} />
            <div>
              <h3 style={cardStyles.empresaNome}>{empresa.nomeFantasia}</h3>
              <p style={cardStyles.razaoSocial}>{empresa.razaoSocial}</p>
            </div>
          </div>
          <div style={{...cardStyles.status, backgroundColor: statusInfo.cor}}>
            <StatusIcon style={cardStyles.statusIcon} />
            <span>{statusInfo.status}</span>
          </div>
        </div>

        {/* Informações da Empresa */}
        <div style={cardStyles.cardBody}>
          <div style={cardStyles.infoGrid}>
            <div style={cardStyles.infoItem}>
              <FaIdCard style={cardStyles.infoIcon} />
              <div>
                <label>CNPJ</label>
                <span>{formatarCNPJ(empresa.cnpj)}</span>
              </div>
            </div>

            <div style={cardStyles.infoItem}>
              <FaUser style={cardStyles.infoIcon} />
              <div>
                <label>Contato</label>
                <span>{empresa.nomeContato || 'Não informado'}</span>
              </div>
            </div>

            <div style={cardStyles.infoItem}>
              <FaPhone style={cardStyles.infoIcon} />
              <div>
                <label>Telefone</label>
                <span>{empresa.ddd ? `(${empresa.ddd}) ` : ''}{formatarTelefone(empresa.telefone)}</span>
              </div>
            </div>

            <div style={cardStyles.infoItem}>
              <FaCalendar style={cardStyles.infoIcon} />
              <div>
                <label>Plano</label>
                <span>{formatarTipoPlano(empresa.tipoPlano)}</span>
              </div>
            </div>

            <div style={cardStyles.infoItem}>
              <FaHdd style={cardStyles.infoIcon} />
              <div>
                <label>Acesso</label>
                <span>
                  {empresa.tipoAcesso === 'WEB'
                    ? `Web · ${empresa.planoArmazenamento ? rotuloPlano(empresa.planoArmazenamento) : 'sem armazenamento definido'}`
                    : 'Local'}
                </span>
              </div>
            </div>

            <div style={cardStyles.infoItem}>
              <FaMapMarkerAlt style={cardStyles.infoIcon} />
              <div>
                <label>Endereço</label>
                <span>{formatarEnderecoEmpresa(empresa)}</span>
              </div>
            </div>
          </div>

          {/* Informações do Plano */}
          <div style={cardStyles.planoInfo}>
            <div style={cardStyles.planoItem}>
              <strong>Período:</strong> {empresa.periodoDias || 30} dias
            </div>
            {empresa.tipoPlano === 'MENSAL' && empresa.diaCobranca && (
              <div style={cardStyles.planoItem}>
                <strong>Dia cobrança:</strong> {empresa.diaCobranca}
              </div>
            )}
            <div style={cardStyles.planoItem}>
              <strong>Dias restantes:</strong> {getDiasRestantes(empresa)}
            </div>
          </div>

          {/* Datas do Período */}
          {(empresa.dataInicioPeriodo || empresa.dataFimPeriodo) && (
            <div style={cardStyles.periodoInfo}>
              {empresa.dataInicioPeriodo && (
                <div style={cardStyles.periodoItem}>
                  <strong>Início:</strong> {formatarData(empresa.dataInicioPeriodo)}
                </div>
              )}
              {empresa.dataFimPeriodo && (
                <div style={cardStyles.periodoItem}>
                  <strong>Fim:</strong> {formatarData(empresa.dataFimPeriodo)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Ações do Card */}
        <div style={cardStyles.cardActions}>
          <button
            onClick={() => handleEdit(empresa)}
            style={cardStyles.editButton}
            title="Editar empresa"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => handleOpenUsuariosModal(empresa)}
            style={cardStyles.usersButton}
            title="Ver usuários"
          >
            <FaUsers />
          </button>
          <button
            onClick={() => handleDeleteConfirmation(empresa)}
            style={cardStyles.deleteButton}
            title="Excluir empresa"
          >
            <FaTrash />
          </button>
        </div>
      </div>
    );
  };

  const inputFieldStyle = (fieldName) => ({
    ...styles.inputField,
    borderColor: errors[fieldName] ? '#dc3545' : '#e9ecef',
    opacity: loading ? 0.7 : 1
  });

  return (
    <div>
      <Header />
      <div style={styles.container}>
        {/* Formulário (mantido igual) */}
        <div id="form-empresa">
          <h2 style={styles.header}>
            {isEditing ? 'Editar Empresa' : 'Cadastro de Empresa'}
          </h2>
          <h4 style={styles.subHeader}>
            Preencha os dados abaixo para {isEditing ? 'editar' : 'cadastrar'} a empresa
          </h4>

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* ... (formulário mantido igual ao anterior) ... */}
            {/* Linha 1 */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>CNPJ *</label>
                <InputMask
                  mask="99.999.999/9999-99"
                  value={formData.cnpj}
                  onChange={(e) => handleInputChange('cnpj', e.target.value)}
                  disabled={loading}
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      type="text"
                      placeholder="00.000.000/0000-00"
                      style={inputFieldStyle('cnpj')}
                    />
                  )}
                </InputMask>
                {errors.cnpj && <span style={styles.errorText}>{errors.cnpj}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Nome Fantasia *</label>
                <InputField
                  value={formData.nomeFantasia}
                  onChange={(e) => handleInputChange('nomeFantasia', e.target.value)}
                  placeholder="Nome Fantasia da Empresa"
                  disabled={loading}
                  style={inputFieldStyle('nomeFantasia')}
                />
                {errors.nomeFantasia && <span style={styles.errorText}>{errors.nomeFantasia}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Razão Social *</label>
                <InputField
                  value={formData.razaoSocial}
                  onChange={(e) => handleInputChange('razaoSocial', e.target.value)}
                  placeholder="Razão Social da Empresa"
                  disabled={loading}
                  style={inputFieldStyle('razaoSocial')}
                />
                {errors.razaoSocial && <span style={styles.errorText}>{errors.razaoSocial}</span>}
              </div>
            </div>

            {/* ... (restante do formulário mantido) ... */}
            {/* Linha 2 */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>DDD *</label>
                <InputField
                  value={formData.ddd}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,3}$/.test(value)) {
                      handleInputChange('ddd', value);
                    }
                  }}
                  placeholder="DDD"
                  disabled={loading}
                  style={inputFieldStyle('ddd')}
                />
                {errors.ddd && <span style={styles.errorText}>{errors.ddd}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Telefone *</label>
                <InputField
                  value={formData.telefone}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,9}$/.test(value)) {
                      handleInputChange('telefone', value);
                    }
                  }}
                  placeholder="999999999"
                  disabled={loading}
                  style={inputFieldStyle('telefone')}
                />
                {errors.telefone && <span style={styles.errorText}>{errors.telefone}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Nome do Contato *</label>
                <InputField
                  value={formData.nomeContato}
                  onChange={(e) => handleInputChange('nomeContato', e.target.value)}
                  placeholder="Nome da pessoa de contato"
                  disabled={loading}
                  style={inputFieldStyle('nomeContato')}
                />
                {errors.nomeContato && <span style={styles.errorText}>{errors.nomeContato}</span>}
              </div>
            </div>

            {/* Endereço completo da empresa — usado para imprimir o cabeçalho do */}
            {/* escritório em documentos gerados pelo sistema, como a Procuração. */}
            <div style={styles.inputGroup}>
              <div style={{ ...styles.fieldContainer, flex: '0 1 180px' }}>
                <label style={styles.label}>CEP</label>
                <InputField
                  value={formData.cep}
                  onChange={(e) => {
                    const formatado = formatarCep(e.target.value);
                    handleInputChange('cep', formatado);
                    if (formatado.replace(/\D/g, '').length === 8) buscarCepEmpresa(formatado);
                  }}
                  placeholder="00000-000"
                  disabled={loading}
                  style={inputFieldStyle('cep')}
                />
                {buscandoCep && <span style={styles.hintText}>Buscando endereço...</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Logradouro</label>
                <InputField
                  value={formData.logradouro}
                  onChange={(e) => handleInputChange('logradouro', e.target.value)}
                  placeholder="Rua Prof. André Puente"
                  disabled={loading}
                  style={inputFieldStyle('logradouro')}
                />
              </div>

              <div style={{ ...styles.fieldContainer, flex: '0 1 120px' }}>
                <label style={styles.label}>Número</label>
                <InputField
                  value={formData.numero}
                  onChange={(e) => handleInputChange('numero', e.target.value)}
                  placeholder="230"
                  disabled={loading}
                  style={inputFieldStyle('numero')}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Complemento</label>
                <InputField
                  value={formData.complemento}
                  onChange={(e) => handleInputChange('complemento', e.target.value)}
                  placeholder="Sala, bloco..."
                  disabled={loading}
                  style={inputFieldStyle('complemento')}
                />
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Bairro</label>
                <InputField
                  value={formData.bairro}
                  onChange={(e) => handleInputChange('bairro', e.target.value)}
                  placeholder="Centro"
                  disabled={loading}
                  style={inputFieldStyle('bairro')}
                />
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Município</label>
                <InputField
                  value={formData.cidade}
                  onChange={(e) => handleInputChange('cidade', e.target.value)}
                  placeholder="Ex: Canguçu"
                  disabled={loading}
                  style={inputFieldStyle('cidade')}
                />
              </div>

              <div style={{ ...styles.fieldContainer, flex: '0 1 100px' }}>
                <label style={styles.label}>UF</label>
                <InputField
                  value={formData.uf}
                  onChange={(e) => handleInputChange('uf', e.target.value.toUpperCase())}
                  placeholder="RS"
                  disabled={loading}
                  style={{ ...inputFieldStyle('uf'), textTransform: 'uppercase' }}
                />
              </div>
            </div>

            {/* Linha 3 */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Tipo Jurídico *</label>
                <select
                  value={formData.tipoJuridico}
                  onChange={(e) => handleInputChange('tipoJuridico', e.target.value)}
                  disabled={loading}
                  style={inputFieldStyle('tipoJuridico')}
                >
                  <option value="">Selecione o Tipo Jurídico</option>
                  <option value="SA">Sociedade Anônima (SA)</option>
                  <option value="LTDA">Sociedade Limitada (LTDA)</option>
                  <option value="EIRELI">Empresa Individual de Responsabilidade Limitada (EIRELI)</option>
                  <option value="MEI">Microempreendedor Individual (MEI)</option>
                  <option value="OUTROS">Outros</option>
                </select>
                {errors.tipoJuridico && <span style={styles.errorText}>{errors.tipoJuridico}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Tipo de Plano *</label>
                <select
                  value={formData.tipoPlano}
                  onChange={(e) => handleInputChange('tipoPlano', e.target.value)}
                  disabled={loading}
                  style={inputFieldStyle('tipoPlano')}
                >
                  <option value="TESTE">Teste</option>
                  <option value="DEFINITIVO">Definitivo</option>
                  <option value="MENSAL">Mensal</option>
                </select>
              </div>

              {formData.tipoPlano === 'MENSAL' && (
                <div style={styles.fieldContainer}>
                  <label style={styles.label}>Dia da Cobrança *</label>
                  <select
                    value={formData.diaCobranca}
                    onChange={(e) => handleInputChange('diaCobranca', parseInt(e.target.value) || 1)}
                    disabled={loading}
                    style={inputFieldStyle('diaCobranca')}
                  >
                    <option value="">Selecione o dia</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((dia) => (
                      <option key={dia} value={dia}>{dia}</option>
                    ))}
                  </select>
                  {errors.diaCobranca && <span style={styles.errorText}>{errors.diaCobranca}</span>}
                </div>
              )}
            </div>

            {/* Acesso: local (servidor próprio, sem limite) ou web (nosso servidor, com plano de armazenamento) */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Tipo de Acesso *</label>
                <select
                  value={formData.tipoAcesso}
                  onChange={(e) => handleInputChange('tipoAcesso', e.target.value)}
                  disabled={loading}
                  style={inputFieldStyle('tipoAcesso')}
                >
                  <option value="LOCAL">Local (servidor próprio)</option>
                  <option value="WEB">Web (acesso pelo nosso servidor)</option>
                </select>
                <span style={styles.hintText}>
                  {formData.tipoAcesso === 'WEB'
                    ? 'Os arquivos enviados contam no armazenamento do plano contratado.'
                    : 'Armazenamento e backup ficam por conta da empresa.'}
                </span>
              </div>

              {formData.tipoAcesso === 'WEB' && (
                <div style={styles.fieldContainer}>
                  <label style={styles.label}>Armazenamento contratado *</label>
                  {!novoPlanoAberto ? (
                    <div style={styles.planoLinha}>
                      <select
                        value={formData.idPlanoArmazenamento}
                        onChange={(e) => handleInputChange('idPlanoArmazenamento', e.target.value)}
                        disabled={loading}
                        style={{ ...inputFieldStyle('idPlanoArmazenamento'), flex: 1 }}
                      >
                        <option value="">Selecione o armazenamento</option>
                        {planosArmazenamento
                          // Plano inativo só aparece se já for o da empresa em edição
                          .filter((plano) => plano.ativo !== false || String(plano.id) === formData.idPlanoArmazenamento)
                          .map((plano) => (
                            <option key={plano.id} value={String(plano.id)}>
                              {rotuloPlano(plano)}{plano.ativo === false ? ' (inativo)' : ''}
                            </option>
                          ))}
                      </select>
                      <button type="button" style={styles.novoPlanoButton} onClick={() => setNovoPlanoAberto(true)} disabled={loading}>
                        + Novo
                      </button>
                    </div>
                  ) : (
                    // Cadastro rápido: cria o plano só com a quantidade de GB e já seleciona para a empresa
                    <div style={styles.planoLinha}>
                      <input
                        type="number"
                        min="1"
                        autoFocus
                        value={novoPlanoGb}
                        onChange={(e) => setNovoPlanoGb(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); criarPlanoRapido(); } }}
                        placeholder="Quantidade em GB (ex.: 5)"
                        disabled={criandoPlano}
                        style={{ ...inputFieldStyle('idPlanoArmazenamento'), flex: 1 }}
                      />
                      <button type="button" style={{ ...styles.novoPlanoButton, backgroundColor: '#4CAF50', color: '#fff', borderColor: '#4CAF50' }}
                        onClick={criarPlanoRapido} disabled={criandoPlano}>
                        {criandoPlano ? '...' : 'Criar'}
                      </button>
                      <button type="button" style={styles.novoPlanoButton} onClick={() => { setNovoPlanoAberto(false); setNovoPlanoGb(''); }} disabled={criandoPlano}>
                        Cancelar
                      </button>
                    </div>
                  )}
                  {errors.idPlanoArmazenamento && <span style={styles.errorText}>{errors.idPlanoArmazenamento}</span>}
                  {!novoPlanoAberto && planosArmazenamento.length === 0 && (
                    <span style={styles.hintText}>Nenhuma opção cadastrada ainda: clique em "+ Novo" e informe os GB.</span>
                  )}
                </div>
              )}
            </div>

            {/* Linha 4 */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Período de Acesso (dias)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.periodoDias}
                  onChange={(e) => handleInputChange('periodoDias', parseInt(e.target.value) || 30)}
                  disabled={loading}
                  style={inputFieldStyle('periodoDias')}
                  placeholder="30"
                />
                {errors.periodoDias && <span style={styles.errorText}>{errors.periodoDias}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>Status</label>
                <select
                  value={formData.ativa}
                  onChange={(e) => handleInputChange('ativa', e.target.value === 'true')}
                  disabled={loading}
                  style={inputFieldStyle('ativa')}
                >
                  <option value={true}>Ativa</option>
                  <option value={false}>Inativa</option>
                </select>
              </div>
            </div>

            {/* Botões */}
            <div style={styles.buttonContainer}>
              <button 
                style={{ 
                  ...styles.button, 
                  backgroundColor: isEditing ? '#2196F3' : '#4CAF50',
                  opacity: loading ? 0.7 : 1
                }} 
                type="submit"
                disabled={loading}
              >
                {loading ? 'Salvando...' : (isEditing ? 'Atualizar Empresa' : 'Cadastrar Empresa')}
              </button>
              
              {isEditing && (
                <>
                  <button 
                    style={{ 
                      ...styles.button, 
                      backgroundColor: '#f44336', 
                      marginLeft: '10px',
                      opacity: loading ? 0.7 : 1
                    }} 
                    onClick={handleCancelEdit}
                    type="button"
                    disabled={loading}
                  >
                    Cancelar Edição
                  </button>
                  
                  {empresaEmEdicao && (
                    <button 
                      type="button"
                      onClick={() => handleOpenUsuariosModal(empresaEmEdicao)}
                      style={styles.usuariosButton}
                    >
                      👥 Ver Usuários
                    </button>
                  )}
                </>
              )}
            </div>
          </form>
        </div>

        <PlanosArmazenamento planos={planosArmazenamento} onAlterado={fetchPlanosArmazenamento} />

        {/* Cards das Empresas */}
        <div style={styles.cardsSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.header}>Empresas Cadastradas</h2>
            <div style={styles.cardsInfo}>
              <span style={styles.cardsCount}>{empresas.length} empresa(s) cadastrada(s)</span>
            </div>
          </div>
          
          {loading ? (
            <div style={styles.loading}>Carregando empresas...</div>
          ) : empresas.length === 0 ? (
            <div style={styles.emptyState}>
              <FaBuilding style={styles.emptyIcon} />
              <h3>Nenhuma empresa cadastrada</h3>
              <p>Cadastre a primeira empresa usando o formulário acima</p>
            </div>
          ) : (
            <div style={styles.cardsGrid}>
              {empresas.map(empresa => (
                <EmpresaCard key={empresa.idEmpresa} empresa={empresa} />
              ))}
            </div>
          )}
        </div>

        {/* Modais */}
        <UsuariosModal
          isOpen={isUsuariosModalOpen}
          onClose={handleCloseUsuariosModal}
          usuarios={usuarios}
          empresaNome={selectedEmpresa}
          loading={usuariosLoading}
        />

        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsConfirmModalOpen(false)}
          message={`Tem certeza que deseja excluir a empresa "${empresaToDelete?.nomeFantasia}"?`}
        />
      </div>
    </div>
  );
}

// Estilos para os Cards
const cardStyles = {
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    marginBottom: '20px',
    border: '1px solid #e9ecef',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 15px rgba(0, 0, 0, 0.15)',
    }
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
    borderBottom: '2px solid #f8f9fa',
    paddingBottom: '15px',
  },
  empresaInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: 1,
  },
  empresaIcon: {
    fontSize: '24px',
    color: '#6f42c1',
    marginTop: '2px',
  },
  empresaNome: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
  },
  razaoSocial: {
    margin: '5px 0 0 0',
    fontSize: '14px',
    color: '#666',
    fontStyle: 'italic',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  statusIcon: {
    fontSize: '12px',
  },
  cardBody: {
    marginBottom: '15px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '15px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  infoIcon: {
    fontSize: '16px',
    color: '#6f42c1',
    minWidth: '16px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    color: '#666',
    fontWeight: '600',
    marginBottom: '2px',
  },
  span: {
    display: 'block',
    fontSize: '14px',
    color: '#333',
    fontWeight: '500',
  },
  planoInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    marginBottom: '10px',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
  },
  planoItem: {
    fontSize: '14px',
    color: '#333',
  },
  periodoInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    padding: '10px',
    backgroundColor: '#e7f3ff',
    borderRadius: '6px',
    border: '1px solid #b3d9ff',
  },
  periodoItem: {
    fontSize: '13px',
    color: '#0066cc',
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '15px',
    borderTop: '1px solid #e9ecef',
  },
  editButton: {
    padding: '8px 12px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#1976d2',
    }
  },
  usersButton: {
    padding: '8px 12px',
    backgroundColor: '#6f42c1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#5a2d91',
    }
  },
  deleteButton: {
    padding: '8px 12px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#c82333',
    }
  },
};

// Estilos principais (mantidos do anterior com ajustes)
const styles = {
  container: {
    padding: '20px',
    width: '95%',
    maxWidth: '1400px',
    margin: 'auto',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  header: {
    color: '#333',
    marginBottom: '10px',
    fontSize: '24px',
    fontWeight: '600',
  },
  subHeader: {
    color: '#666',
    marginBottom: '20px',
    fontSize: '16px',
    fontWeight: 'normal',
  },
  form: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    marginBottom: '30px',
  },
  inputGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    marginBottom: '25px',
  },
  fieldContainer: {
    flex: '1 1 calc(33.333% - 20px)',
    minWidth: '280px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333',
    fontSize: '14px',
  },
  inputField: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    backgroundColor: '#fff',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  errorText: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '5px',
    display: 'block',
  },
  planoLinha: {
    display: 'flex',
    gap: '8px',
    alignItems: 'stretch',
  },
  novoPlanoButton: {
    padding: '0 14px',
    border: '2px solid #2196F3',
    borderRadius: '6px',
    backgroundColor: '#fff',
    color: '#2196F3',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  hintText: {
    color: '#666',
    fontSize: '11px',
    marginTop: '5px',
    display: 'block',
    fontStyle: 'italic',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '15px',
    marginTop: '30px',
    flexWrap: 'wrap',
  },
  button: {
    padding: '12px 25px',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    minWidth: '160px',
  },
  usuariosButton: {
    padding: '12px 20px',
    backgroundColor: '#6f42c1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
  },
  cardsSection: {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  cardsInfo: {
    color: '#666',
    fontSize: '14px',
  },
  cardsCount: {
    backgroundColor: '#e7f3ff',
    padding: '8px 15px',
    borderRadius: '20px',
    fontWeight: '600',
    color: '#0066cc',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '20px',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
    fontSize: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666',
  },
  emptyIcon: {
    fontSize: '48px',
    color: '#dee2e6',
    marginBottom: '15px',
  },
};

export default EmpresaRegistration;
